import type { MeasurementStage, Sample } from '../api/types';
import { STAGE_LABEL } from '../lib/labels';
import { time } from '../lib/time';

const W = 800, H = 280, L = 44, R = 12, T = 28, B = 30;
const SERIES = ['var(--ink)', 'var(--eco)'];
const DASH = ['', '6 4'];

/** Courbes des deux sondes (séries brutes, jamais moyennées) avec étapes et cibles. */
export function TempChart({ samples, sensorIds, stages, high, low, names }: {
  samples: Sample[];
  sensorIds: string[];
  stages?: MeasurementStage[] | null;
  high?: number | null;
  low?: number | null;
  names: Record<string, string>;
}) {
  if (!samples.length) return <p className="empty">Aucun échantillon pour ce test.</p>;
  const ts = samples.map((s) => new Date(s.observed_at).getTime());
  const t0 = Math.min(...ts), t1 = Math.max(...ts, t0 + 60_000);
  const temps = samples.map((s) => s.temperature).concat(high ?? [], low ?? []);
  const lo = Math.floor(Math.min(...temps) - 0.5), hi = Math.ceil(Math.max(...temps) + 0.5);
  const x = (t: number) => L + ((t - t0) / (t1 - t0)) * (W - L - R);
  const y = (c: number) => T + (1 - (c - lo) / (hi - lo)) * (H - T - B);
  const ids = sensorIds.length ? sensorIds : [...new Set(samples.map((s) => s.sensor_id))];
  const ticks = Array.from({ length: hi - lo + 1 }, (_, i) => lo + i).filter((_, i, a) => a.length <= 8 || i % 2 === 0);
  const hours = Math.ceil((t1 - t0) / 3_600_000);

  return (
    <figure className="chart">
      <div className="legend">
        {ids.map((id, i) => (
          <span key={id}><svg width="18" height="6" aria-hidden="true"><line x1="0" x2="18" y1="3" y2="3" stroke={SERIES[i % 2]} strokeWidth="3" strokeDasharray={DASH[i % 2]} /></svg>{names[id] ?? id}</span>
        ))}
        {(high || low) && <span><svg width="18" height="6" aria-hidden="true"><line x1="0" x2="18" y1="3" y2="3" stroke="var(--muted)" strokeDasharray="3 3" /></svg>Cibles {high ?? '–'} / {low ?? '–'} °C</span>}
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={`Températures de ${ids.length} sondes de ${time(new Date(t0).toISOString())} à ${time(new Date(t1).toISOString())}`}>
        {(stages ?? []).map((s, i) => {
          const a = s.confirmed_at ?? s.requested_at, b = s.ended_at;
          if (!a) return null;
          const xa = x(Math.max(t0, new Date(a).getTime())), xb = x(b ? new Date(b).getTime() : t1);
          return (
            <g key={i}>
              <rect x={xa} y={T} width={Math.max(0, xb - xa)} height={H - T - B} className={s.stage === 'cooling' ? 'band-cool' : 'band-heat'} />
              <text x={xa + 6} y={T - 8} className="chart-label">{STAGE_LABEL[s.stage]}</text>
            </g>
          );
        })}
        {ticks.map((c) => (
          <g key={c}>
            <line x1={L} x2={W - R} y1={y(c)} y2={y(c)} className="grid-line" />
            <text x={L - 8} y={y(c) + 4} textAnchor="end" className="chart-tick">{c}</text>
          </g>
        ))}
        {Array.from({ length: hours + 1 }, (_, h) => h).filter((h) => hours <= 6 || h % 2 === 0).map((h) => (
          <text key={h} x={x(t0 + h * 3_600_000)} y={H - 10} textAnchor="middle" className="chart-tick">{h} h</text>
        ))}
        {high != null && <line x1={L} x2={W - R} y1={y(high)} y2={y(high)} className="target-line" />}
        {low != null && <line x1={L} x2={W - R} y1={y(low)} y2={y(low)} className="target-line" />}
        {ids.map((id, i) => {
          const pts = samples.filter((s) => s.sensor_id === id).sort((a, b) => a.observed_at.localeCompare(b.observed_at))
            .map((s) => `${x(new Date(s.observed_at).getTime()).toFixed(1)},${y(s.temperature).toFixed(1)}`).join(' ');
          return <polyline key={id} points={pts} fill="none" stroke={SERIES[i % 2]} strokeWidth="2" strokeDasharray={DASH[i % 2]} />;
        })}
      </svg>
    </figure>
  );
}
